<template>
    <div
        class="d-flex flex-column"
    >
        <template
            v-for="(productOpt, index) in optionsList"
            :key="`product-option-${index}`"
        >
            <article
                class="product-option"
                :class="{
                    'order-first': productOpt.isProminent
                }"
            >
                <!-- Default Value / Single Selection Option -->
                <div
                    v-if="productOpt.isProminent"
                >
                    <DebugContent>
                        <ul class="font-size-7">
                            <li><strong>DEFAULT ITEM (isProminent)</strong></li>
                            <li>
                                ID:
                                <strong class="bg-secondary px-1">{{ productOpt.attribute.uid }}</strong>
                            </li>
                        </ul>
                    </DebugContent>
                    <label class="d-flex text-uppercase font-weight-bold">
                        {{ productOpt.attribute.name }}
                    </label>

                    <ProductDefaultOption
                        :option="productOpt"
                        no-event
                        block
                    ></ProductDefaultOption>
                </div>

                <!-- TODO: Remove - deprecated (use above) -->
                <div
                    v-else-if="productOpt.groupValues.length <= 1 && optionsModel[productOpt.attribute.uid]"
                >
                    <DebugContent>
                        <ul class="font-size-7">
                            <li><strong>DEFAULT VALUE ITEM</strong></li>
                            <li>
                                ID:
                                <strong class="bg-secondary px-1">{{ productOpt.attribute.uid }}</strong>
                            </li>
                            <li>
                                SELECTED:
                                <strong class="bg-success px-1">{{ optionsModel[productOpt.attribute.uid] }}</strong>
                            </li>
                        </ul>
                    </DebugContent>
                    <label class="d-flex text-uppercase font-weight-bold">
                        {{ productOpt.attribute.name }}
                    </label>
                    <ProductDefaultValueInput
                        v-model="optionsModel[productOpt.attribute.uid]!"
                        :name="productOpt.attribute.name"
                        :option="productOpt"
                        no-event
                        block
                    ></ProductDefaultValueInput>
                </div>

                <!-- Color Option -->
                <div
                    v-else-if="productOpt.attribute.isColor || productOpt.attribute.name === 'Color'"
                    :key="`option-color-${index}`"
                >
                    <label class="d-flex text-uppercase font-weight-bold">
                        {{ productOpt.attribute.name }}
                    </label>
                    <DebugContent>
                        <ul class="font-size-7">
                            <li>
                                ID:
                                <strong class="bg-secondary px-1">{{ productOpt.attribute.uid }}</strong>
                            </li>
                            <li v-if="productOpt.attribute.uid">
                                SELECTED:
                                <strong class="bg-success px-1">{{ optionsModel[productOpt.attribute.uid] }}</strong>
                            </li>
                            <li>
                                <div>OPTIONS UIDS:</div>
                                <strong>
                                {{ productOpt.groupValues.map((v) => v.value.uid)?.join(' ○ ') }}
                                </strong>
                            </li>
                        </ul>
                    </DebugContent>
                    <v-input
                        v-model="optionsModel[productOpt.attribute.uid!]"
                    >
                        <ColorBtnGroupInput
                            v-model="optionsModel[productOpt.attribute.uid!]!"
                            class="fill-width"
                            :class="{ 'sym-layout': !isMobile }"
                            :option="productOpt"
                            :options-disabled-model-set="optionsDisabledModel?.[productOpt.attribute.uid]"
                            @update:model-value="onChange(productOpt.attribute.uid, $event, index)"
                        ></ColorBtnGroupInput>
                    </v-input>
                </div>

                <!-- Common Options -->
                <div
                    v-else
                >
                    <label class="d-flex text-uppercase font-weight-bold justify-space-between">
                        <span>{{ productOpt.attribute.name }}</span>
                        <span v-if="productOpt.attribute.name === 'Fabric'">
                            <GarmentFabricGuide
                                class="text-none text-subtitle-2 no-underline-link font-weight-bold"
                            ></GarmentFabricGuide>
                        </span>
                    </label>
                    <DebugContent>
                        <ul class="font-size-7">
                            <li>
                                ID:
                                <strong class="bg-secondary px-1">{{ productOpt.attribute.uid }}</strong>
                            </li>
                            <li>
                                SELECTED:
                                <strong class="bg-success px-1">{{ optionsModel[productOpt.attribute.uid] }}</strong>
                            </li>
                            <li>
                                <div>OPTIONS UIDS:</div>
                                <strong>
                                {{ productOpt.groupValues.map((v) => v.value.uid)?.join(' ○ ') }}
                                </strong>
                            </li>
                        </ul>
                    </DebugContent>
                    <v-input
                        v-model="optionsModel[productOpt.attribute.uid]"
                    >
                        <OptionBtnGroupInput
                            v-model="optionsModel[productOpt.attribute.uid]!"
                            class="fill-width"
                            :option="productOpt"
                            :mandatory="true"
                            @update:model-value="onChange(productOpt.attribute.uid, $event, index)"
                            tall-buttons
                        ></OptionBtnGroupInput>
                    </v-input>
                </div>
            </article>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type {
        ProductOptionsModel,
        ProductAttrsDisabledModel,
        ProductAttrsGroupOption
    } from '~/types';
    import {
        useIsMobile
    } from '~/composables';

    import ColorBtnGroupInput from '~/components/controls/ColorBtnGroupInput/ColorBtnGroupInput.vue';
    import OptionBtnGroupInput from '~/components/controls/OptionBtnGroupInput/OptionBtnGroupInput.vue';
    import ProductDefaultValueInput from '~/components/controls/ProductDefaultValueInput/ProductDefaultValueInput.vue';
    import ProductDefaultOption from '~/components/products/product/ProductDefaultOption/ProductDefaultOption.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';

    const GarmentFabricGuide = defineAsyncComponent(() => import('~/components/products/apparel/GarmentFabricGuide/GarmentFabricGuide.vue'));

    defineProps<{
        optionsModel: ProductOptionsModel;
        optionsDisabledModel?: ProductAttrsDisabledModel;
        optionsList: ProductAttrsGroupOption[];
    }>();

    const emit = defineEmits<{
        'options-model': [value: [uidAtt: string, uidAttVal: string, optionIndex: number]];
    }>();

    const { isMobile } = useIsMobile();

    async function onChange(
        uidAtt: string,
        uidAttVal: string,
        optionIndex: number
    ): Promise<void> {
        emit('options-model', [uidAtt, uidAttVal, optionIndex]);
    }
</script>

<style lang="scss"></style>
